export type WealthBand = "Low" | "Medium" | "High" | "Very High";

export interface StartingPossessionsInput {
  speciesId: string;
  originId: string;
  clanName: string;
  characterName: string;
  buildLabel: string;
}

export interface StartingPossessions {
  wealth: WealthBand;
  yen: string;
  inventory: string[];
  armor: string;
  weapons: Array<Record<string, never>>;
}

interface PossessionPackage {
  wealth: WealthBand;
  cashYen: number;
  items: string[];
}

const SPECIES_PACKAGES: Record<string, PossessionPackage[]> = {
  "species.kappa": [
    {
      wealth: "Low",
      cashYen: 12_000,
      items: [
        "Waterproof smartphone in a sealed case",
        "Watertight document pouch",
        "Hand-drawn map of local rivers, canals, and drains",
        "Cloth-wrapped cucumber offering",
        "Compact flashlight",
      ],
    },
    {
      wealth: "Medium",
      cashYen: 28_000,
      items: [
        "Rugged waterproof phone",
        "Prepaid transit card",
        "Maintenance keys for a riverside facility",
        "Folding rain cape",
        "Old sumo tournament ticket and a small first-aid kit",
      ],
    },
    {
      wealth: "Medium",
      cashYen: 36_000,
      items: [
        "Smartphone with waterproof power bank",
        "Waterproof notebook and grease pencil",
        "Fishing line, hooks, and a compact repair kit",
        "River-stone charm from a familiar waterway",
        "Loose human clothing suitable for a disguise",
      ],
    },
  ],
  "species.kitsune": [
    {
      wealth: "High",
      cashYen: 120_000,
      items: [
        "Elegant high-quality clothing",
        "Gemstone necklace and compact jewelry case",
        "Tail-grooming and cosmetics kit",
        "Two smartphones registered under different identities",
        "Lacquered business-card case",
      ],
    },
    {
      wealth: "Medium",
      cashYen: 65_000,
      items: [
        "Carefully tailored city clothes",
        "Compact mirror and grooming case",
        "Prepaid phone and transit card",
        "Gift envelopes for favors and introductions",
        "Small Inari shrine keepsake",
      ],
    },
    {
      wealth: "Very High",
      cashYen: 300_000,
      items: [
        "Designer coat and formal evening wear",
        "Precious-stone jewelry chosen to display status",
        "Secure smartphone and spare prepaid handset",
        "Private-club membership card",
        "Silk case containing grooming tools for the tails",
      ],
    },
  ],
  "species.oni": [
    {
      wealth: "Low",
      cashYen: 15_000,
      items: [
        "Oversized work clothes and sturdy boots",
        "Industrial gloves",
        "Heavy-duty phone with a cracked protective case",
        "Large duffel bag",
        "Metal thermos and basic repair tools",
      ],
    },
    {
      wealth: "Medium",
      cashYen: 40_000,
      items: [
        "Loose hooded clothing suitable for concealing horns",
        "Reinforced work boots",
        "Prepaid phone",
        "Construction-site access card",
        "Clan cash envelope and a compact first-aid kit",
      ],
    },
    {
      wealth: "High",
      cashYen: 90_000,
      items: [
        "Black suit tailored for an unusually large frame",
        "Sturdy dress shoes",
        "Encrypted smartphone",
        "Business cards for a human-facing occupation",
        "Heavy leather travel bag",
      ],
    },
  ],
  "species.yamabushi_tengu": [
    {
      wealth: "Low",
      cashYen: 8_000,
      items: [
        "Patched pilgrim cloak",
        "Tall geta sandals",
        "Hand-crafted ha-uchiwa made from wing feathers",
        "Mountain trail map",
        "Prayer beads and a weatherproof satchel",
      ],
    },
    {
      wealth: "Medium",
      cashYen: 22_000,
      items: [
        "Austere travel clothes",
        "Feather-and-straw cloak",
        "Topographic maps and compact binoculars",
        "Climbing rope and field radio",
        "Hand-crafted ha-uchiwa",
      ],
    },
    {
      wealth: "Medium",
      cashYen: 32_000,
      items: [
        "Weatherproof mountain clothing",
        "Tall geta wrapped for travel",
        "Wing-feather ha-uchiwa",
        "Thermos, trail rations, and first-aid supplies",
        "Shrine seal and handwritten record of Clan obligations",
      ],
    },
  ],
};

const DEFAULT_PACKAGES: PossessionPackage[] = [
  {
    wealth: "Medium",
    cashYen: 30_000,
    items: [
      "Smartphone",
      "Prepaid transit card",
      "Everyday clothing",
      "Personal keepsake",
      "Small work bag",
    ],
  },
];

function stableHash(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function pick<T>(values: readonly T[], seed: number): T {
  return values[seed % values.length]!;
}

function originKeepsake(originId: string): string {
  const normalized = originId.toLowerCase();
  if (normalized.includes("evolution")) {
    return "A keepsake from the creature, human life, or old object that existed before the transformation";
  }
  if (normalized.includes("nature")) {
    return "A family token connected to a yōkai parent or the place of birth";
  }
  if (normalized.includes("scarecrow") || normalized.includes("spauracchio")) {
    return "A small object taken from the place or moment whose human Fear gave the yōkai form";
  }
  return "A personal origin keepsake";
}

function clanPossession(clanName: string): string {
  const normalized = clanName.toLowerCase();
  if (normalized.includes("last train")) return "Clan-issued station locker key and late-night rail pass";
  if (normalized.includes("rain-stained lantern")) return "Stormproof pocket lantern bearing the Clan mark";
  if (normalized.includes("crooked torii")) return "Folded map of neglected shrines and hidden passages";
  if (normalized.includes("seventh bell")) return "Coded notebook of warnings, debts, and supernatural contacts";
  if (normalized.includes("red thread")) return "Bundle of red cord and sealed gift envelopes for obligations";
  if (normalized.includes("quiet current")) return "Waterproof route ledger for concealed paths through the city";
  if (normalized.includes("black pine")) return "Territory keys and a field notebook marked with the Clan emblem";
  if (normalized.includes("empty mask")) return "Spare identity papers and a set of carefully prepared business cards";
  if (normalized.includes("white cinders")) return "Heatproof pouch containing an ash-stained Clan token";
  if (normalized.includes("glass moon")) return "Compact camera and power bank used to track modern legends";
  return "Clan token identifying the bearer as a member in good standing";
}

function formatYen(value: number): string {
  return `¥${value.toLocaleString("en-US")}`;
}

export function generateStartingPossessions(input: StartingPossessionsInput): StartingPossessions {
  const seed = stableHash([
    input.speciesId,
    input.originId,
    input.clanName,
    input.characterName,
    input.buildLabel,
  ].join("|"));
  const packages = SPECIES_PACKAGES[input.speciesId] ?? DEFAULT_PACKAGES;
  const selected = pick(packages, seed);
  const inventory = [
    `Starting wealth: ${selected.wealth} (subject to Sensei approval)`,
    ...selected.items,
    originKeepsake(input.originId),
    clanPossession(input.clanName),
  ];

  return {
    wealth: selected.wealth,
    yen: formatYen(selected.cashYen),
    inventory,
    armor: "",
    weapons: [],
  };
}
